import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, Send, CheckCircle2, ArrowRight, Sparkles, Phone, Mail, Building2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { captureCrmLead } from "../lib/crm";
import { SERVICE_OPTIONS, BUDGET_OPTIONS, PRIMARY_EMAIL, PRIMARY_PHONE } from "../data";

export function openConsultationModal(service = "") {
  window.dispatchEvent(new CustomEvent("open-consultation", { detail: { service } }));
}

export const ConsultationModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });

  useEffect(() => {
    const handleOpen = (e) => {
      const initialService = e.detail?.service || "";
      setForm({ name: "", email: "", phone: "", company: "", service: initialService, budget: "", message: "" });
      setSubmitted(false);
      setIsOpen(true);
    };
    window.addEventListener("open-consultation", handleOpen);
    return () => window.removeEventListener("open-consultation", handleOpen);
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and project details.");
      return;
    }
    setLoading(true);
    try {
      const serviceName = form.service ? `Consultation: ${form.service}` : "Book Consultation Request";
      
      // Direct CRM webhook capture
      captureCrmLead({
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: serviceName,
        notes: form.message,
        company: form.company,
        budget: form.budget,
      });

      await api.post("/contact", {
        ...form,
        service: serviceName,
      });

      setSubmitted(true);
      toast.success("Consultation request received! Our team will reach out within 24 hours.");
    } catch {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/70 backdrop-blur-md"
          />

          {/* Modal Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="relative w-full max-w-2xl bg-white rounded-3xl p-6 sm:p-9 shadow-2xl z-10 my-auto border border-black/10 overflow-hidden"
          >
            {/* Ambient Background Blob */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#FF5A1F]/10 rounded-full blur-3xl pointer-events-none" />

            {/* Close Button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-5 right-5 w-10 h-10 rounded-full bg-neutral-100 grid place-items-center text-neutral-500 hover:text-black hover:bg-neutral-200 transition-colors z-20"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {submitted ? (
              <div className="text-center py-10 space-y-4">
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mx-auto">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#0B0B0B]">Consultation Requested!</h3>
                <p className="text-neutral-500 max-w-md mx-auto text-sm sm:text-base leading-relaxed">
                  Thank you, <span className="font-semibold text-neutral-800">{form.name}</span>. Our engineering leaders will review your requirements and get back to you within <span className="font-semibold text-[#FF5A1F]">1 business day</span> with an actionable roadmap.
                </p>
                <div className="pt-4">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="px-8 py-3 rounded-full bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] transition-colors shadow-md shadow-[#FF5A1F]/20"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F] text-xs font-bold uppercase tracking-wider mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F]" /> Tech Strategy Session
                  </div>

                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0B0B0B]">Book a Free Consultation</h2>
                  <p className="text-neutral-500 text-sm mt-1">
                    Discuss your project architecture, tech stack, timeline, and estimates with our senior engineers.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-neutral-700 mb-1">Your Full Name *</label>
                      <input
                        required
                        type="text"
                        placeholder="e.g. Rahul Sharma"
                        className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F] transition-colors text-[#0B0B0B]"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-700 mb-1">Work Email *</label>
                      <input
                        required
                        type="email"
                        placeholder="rahul@company.com"
                        className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F] transition-colors text-[#0B0B0B]"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-neutral-700 mb-1">Phone / WhatsApp</label>
                      <input
                        type="text"
                        placeholder="+91 98765 43210"
                        className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F] transition-colors text-[#0B0B0B]"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-700 mb-1">Company / Organization</label>
                      <input
                        type="text"
                        placeholder="e.g. AcroTech Pvt Ltd"
                        className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F] transition-colors text-[#0B0B0B]"
                        value={form.company}
                        onChange={(e) => set("company", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-neutral-700 mb-1">Primary Area of Interest</label>
                      <select
                        className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F] transition-colors text-[#0B0B0B]"
                        value={form.service}
                        onChange={(e) => set("service", e.target.value)}
                      >
                        <option value="">Select Service / Solution</option>
                        {SERVICE_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-neutral-700 mb-1">Estimated Budget</label>
                      <select
                        className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F] transition-colors text-[#0B0B0B]"
                        value={form.budget}
                        onChange={(e) => set("budget", e.target.value)}
                      >
                        <option value="">Select Budget Range</option>
                        {BUDGET_OPTIONS.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-neutral-700 mb-1">Tell Us About Your Project & Goals *</label>
                    <textarea
                      required
                      rows={3}
                      placeholder="Briefly describe your requirements, challenges, or goals..."
                      className="w-full bg-[#FAF9F6] border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF5A1F] transition-colors resize-none text-[#0B0B0B]"
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                    />
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    <p className="text-[11px] text-neutral-400 text-center sm:text-left">
                      🔒 Your information is confidential & protected under NDA.
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] transition-colors disabled:opacity-60 shadow-lg shadow-[#FF5A1F]/25 shrink-0"
                    >
                      {loading ? "Scheduling..." : "Book Free Consultation"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
